<template>
    <div class="ppv2-relative" :style="customStyle">
        <!-- Label with required asterisk -->
        <label class="ppv2-text-black mb-2 d-block" :class="customLabelClass" :style="{ fontSize: normalizeFontSize(15) }">
            {{ label }}<span v-if="required" class="text-purple">*</span>
        </label>
        <span
            v-if="description"
            class="ppv2-text-base ppv2-text-black ppv2-text-opacity-70"
            :style="{ fontSize: normalizeFontSize(14) }"
        >
            {{ description }}
        </span>

        <!-- Wrapper with tooltip -->
        <v-tooltip bottom :disabled="!disabled || !tooltipMsg" max-width="300">
            <template v-slot:activator="{ on, attrs }">
                <div class="ppv2-relative" v-bind="attrs" v-on="on">
                    <!-- List of selectable items -->
                    <v-list
                        class="checkList"
                        :class="[
                            { '!ppv2-flex ppv2-flex-row ppv2-flex-wrap ppv2-justify-center': inSameLine },
                            { 'ppv2-cursor-not-allowed': disabled },
                            { 'ppv2-border ppv2-border-red-500 ppv2-rounded-lg': errorText },
                        ]"
                        :style="{ gap: inSameLine ? (isMdOrBelow ? '16px' : normalizeSize(50, true)) : null }"
                    >
                        <v-list-item
                            v-for="option in options"
                            :key="option.value"
                            @click="selectOption(option)"
                            :class="[
                                customClassItem,
                                'my-2 rounded-12',
                                { border: !withoutBorder },
                                { 'ppv2-max-w-[200px]': inSameLine && !hasHalfWidth },
                                { 'md:ppv2-max-w-[45%]': inSameLine && hasHalfWidth },
                                { 'border-primary bg-blue-lighten-5': value === option.value && !withoutBorder },
                                { 'disabled-item': disabled || option.disabled },
                            ]"
                            ripple
                        >
                            <!-- Left icon -->
                            <v-list-item-action class="mr-2">
                                <v-icon v-if="value === option.value" color="primary" :size="normalizeSize(16, true)">
                                    mdi-check
                                </v-icon>
                                <v-icon v-else color="grey lighten-1" :size="normalizeSize(16, true)">
                                    mdi-checkbox-blank-circle
                                </v-icon>
                            </v-list-item-action>

                            <!-- Text content -->
                            <v-list-item-content>
                                <v-list-item-title
                                    class="ppv2-text-black !ppv2-whitespace-normal"
                                    :class="[{ '!ppv2-text-[#b0b0b0]': option.disabled }]"
                                    :style="{ fontSize: normalizeFontSize(14) }"
                                    >{{ option.label }}</v-list-item-title
                                >
                                <v-list-item-subtitle
                                    v-if="option.subtitle"
                                    class="!ppv2-text-[#7B8289] !ppv2-whitespace-normal"
                                    :style="{ fontSize: normalizeFontSize(12) }"
                                >
                                    {{ option.subtitle }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </div>
            </template>
            <span v-if="tooltipMsg">{{ tooltipMsg }}</span>
        </v-tooltip>

        <!-- Error message -->
        <div v-if="errorText" class="ppv2-text-red-500 ppv2-text-sm ppv2-mt-1" :style="{ fontSize: normalizeFontSize(12) }">
            {{ errorText }}
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../utils/normalizeSize'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'SelectableList',
    components: {},
    props: {
        label: String,
        description: String,
        withoutBorder: {
            type: Boolean,
            default: false,
        },
        customLabelClass: String,
        inSameLine: Boolean,
        hasHalfWidth: Boolean,
        customClassItem: String,
        options: {
            type: Array,
            required: true,
        },
        value: {
            // Vue 2 uses "value" for v-model
            type: [String, Number, Boolean, null],
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        tooltipMsg: {
            type: String,
            default: '',
        },
        customStyle: {
            type: Object,
            default: () => ({}),
        },
        // ✅ NEW: Required field indicator
        required: {
            type: Boolean,
            default: false,
        },
        // ✅ NEW: Error message display
        errorText: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isMdOrBelow: typeof window !== 'undefined' ? window.innerWidth <= 999 : false,
        }
    },
    mounted() {
        this.resizeHandler = () => {
            this.isMdOrBelow = window.innerWidth <= 999
        }
        window.addEventListener('resize', this.resizeHandler)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.resizeHandler)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        selectOption(option) {
            if (this.disabled || option.disabled) return
            this.$emit('input', option.value) // Vue 2 listens to "input" event
        },
    },
}
</script>

<style scoped>
.checkList {
    background-color: transparent !important;
    padding: 0 !important;
}
.border {
    border: 1px solid #9c9af880 !important;
}
.border-primary {
    border-color: #7d2dbd !important;
}
.bg-blue-lighten-5 {
    background-color: #1a1f370d !important;
}
.dark .bg-blue-lighten-5 {
    background-color: #d9d9d91a !important;
}
.grey.lighten-1::before {
    border-color: #7b8289 !important;
    background-color: #7b8289 !important;
}
.v-application .v-icon.mdi.text--primary {
    color: #fff !important;
    caret-color: #fff !important;
    background: #7d2dbd !important;
    border-radius: 50%;
}
.disabled-item {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed !important;
}
.ppv2-cursor-not-allowed {
    cursor: not-allowed !important;
}
.ppv2-cursor-not-allowed * {
    cursor: not-allowed !important;
}
.tooltip-wrapper {
    position: relative;
}
@media (max-width: 768px) {
    .checkList .my-2.rounded-12.border.ppv2-max-w-\[200px\] {
        width: max-content;
        min-width: max-content;
        max-width: max-content;
        margin: 0 7px;
    }
}
</style>
